import { isExplicitPermissionKey } from "../../../Permissions/explicitPermissionKeys";

export enum UserStateEnum {
  ACTIVE = "active",
  INACTIVE = "inactive",
  EXPIRED = "expired",
  INVITED = "invited",
}

export const MembershipFlag = {
  TASK_VIEWER: "task_viewer",
  TASK_CREATOR: "task_creator",
  TASK_EXECUTOR: "task_executor",
  TASK_SUPERVISOR: "task_supervisor",
} as const;

export type MembershipFlagValue = typeof MembershipFlag[keyof typeof MembershipFlag];
export type MembershipFlagsValue = MembershipFlagValue[] | number;

export const MembershipFlagBitmask: Record<MembershipFlagValue, number> = {
  [MembershipFlag.TASK_VIEWER]: 1,
  [MembershipFlag.TASK_CREATOR]: 2,
  [MembershipFlag.TASK_EXECUTOR]: 4,
  [MembershipFlag.TASK_SUPERVISOR]: 8,
};

export function hasMembershipFlag(membershipFlags: MembershipFlagsValue, flag: MembershipFlagValue): boolean {
  if (Array.isArray(membershipFlags)) {
    return membershipFlags.includes(flag);
  }

  return (membershipFlags & MembershipFlagBitmask[flag]) !== 0;
}

export function normalizeMembershipFlags(membershipFlags: MembershipFlagsValue): MembershipFlagValue[] {
  if (Array.isArray(membershipFlags)) {
    return membershipFlags;
  }

  return Object.values(MembershipFlag).filter(flag => hasMembershipFlag(membershipFlags, flag));
}

export function sortMembershipFlagsByPriority(membershipFlags: MembershipFlagsValue): MembershipFlagValue[] {
  return normalizeMembershipFlags(membershipFlags).sort(
    (a, b) => MembershipFlagBitmask[b] - MembershipFlagBitmask[a],
  );
}

export function getHighestMembershipFlag(membershipFlags: MembershipFlagsValue): MembershipFlagValue | null {
  const sorted = sortMembershipFlagsByPriority(membershipFlags);

  return sorted[0] ?? null;
}

/**
 * Task attribute strings that drive membership flags. Kept local to avoid a
 * dependency on the full permissions enum. Mirrors the server-side
 * `App\Security\Permission\Attribute\TaskAttribute` constants.
 */
const TaskMembershipAttribute = {
  READ_ASSIGNED_DIRECTLY_TO_SCOPE: "context.task.read.assigned_directly_to_scope",
  CREATE_ASSIGNED_DIRECTLY_TO_SCOPE: "context.task.create.assigned_directly_to_scope",
  UPDATE_ASSIGNED_DIRECTLY_TO_SCOPE: "context.task.update.assigned_directly_to_scope",
  STATUS_TO_CHECKED: "context.task.update.field_value.status.to_checked",
} as const;

/**
 * Attributes required for each membership flag. Mirrors the server-side
 * `App\Enum\MembershipFlag::ATTRIBUTE_MAP`.
 */
const MembershipFlagAttributeMap: Record<MembershipFlagValue, string[]> = {
  [MembershipFlag.TASK_VIEWER]: [TaskMembershipAttribute.READ_ASSIGNED_DIRECTLY_TO_SCOPE],
  [MembershipFlag.TASK_CREATOR]: [TaskMembershipAttribute.CREATE_ASSIGNED_DIRECTLY_TO_SCOPE],
  [MembershipFlag.TASK_EXECUTOR]: [TaskMembershipAttribute.UPDATE_ASSIGNED_DIRECTLY_TO_SCOPE],
  [MembershipFlag.TASK_SUPERVISOR]: [
    TaskMembershipAttribute.UPDATE_ASSIGNED_DIRECTLY_TO_SCOPE,
    TaskMembershipAttribute.STATUS_TO_CHECKED,
  ],
};

/**
 * Determines whether an attribute is granted by a list of attributes, taking
 * ancestor wildcards into account (e.g. `context.task.update.*` grants
 * `context.task.update.assigned_directly_to_scope`). An explicit key is granted only by itself.
 *
 * Mirrors `App\Security\Voter\PermissionAttribute\AbstractPermissionAttributeVoter::hasPermission`.
 */
export function hasPermissionAttribute(attribute: string, attributes: string[]): boolean {
  if (attributes.includes(attribute)) {
    return true;
  }

  if (isExplicitPermissionKey(attribute)) {
    return false;
  }

  const parts = attribute.split(".");
  if (parts.length < 3) {
    return false;
  }

  // The last element is never used to build a wildcard.
  parts.pop();

  const previousParts: string[] = [];
  for (const part of parts) {
    const wildcard = `${[...previousParts, part].join(".")}.*`;
    if (attributes.includes(wildcard)) {
      return true;
    }
    previousParts.push(part);
  }

  return false;
}

/**
 * Precalculates the membership flags that a set of attributes would resolve to,
 * mirroring the server-side `App\Enum\MembershipFlag::resolve`. Useful for
 * showing the resulting membership before persisting a bracket.
 */
export function resolveMembershipFlags(attributes: string[]): MembershipFlagValue[] {
  const flags = Object.values(MembershipFlag).filter((flag) => {
    return MembershipFlagAttributeMap[flag].every(required => hasPermissionAttribute(required, attributes));
  });

  return sortMembershipFlagsByPriority(flags);
}
